import type { OptionSource } from '@ns/plugin-picker-toolbox-contracts';

/**
 * The flat option shape the BUI Combobox/Select consume: a stable string
 * `value` (the option identity) and a display `label`.
 */
export type ProjectedOption = {
  label: string;
  value: string;
};

/**
 * A resolved option paired with the full value object the picker writes to the
 * form and the raw source item. `id` is the stable identity (used as the widget
 * key), `label` is the display text, `optionValue` is the WHOLE
 * `getOptionValue(item)` object (default `{ id, name }`), and `item` is the raw
 * source item kept so an enrichable source can re-project it after
 * `resolveSelection`. The selection handler writes `optionValue`, never just the
 * id, so a template consumes `${{ parameters.field.name }}` and friends.
 */
export type ResolvedOption<TItem, TValue> = {
  id: string;
  item: TItem;
  label: string;
  optionValue: TValue;
};

/**
 * Reads the option identity from an adapter-projected value under the effective
 * `{ id, name }` regime. The contract type leaves `TValue`
 * generic, but the shared contract suite every driven adapter runs green asserts
 * `getOptionValue(item).id` is a stable, unique string. So the picker treats the
 * `.id` field as identity. An adapter that does not yield `{ id, name }` falls
 * outside the suite and is not supported by this picker.
 *
 * Pure: reads a field, never calls a resolve path.
 */
export function optionValueId(value: unknown): string | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const id: unknown = Reflect.get(value, 'id');
  return typeof id === 'string' ? id : undefined;
}

/** Reads the `.name` field from an adapter-projected value, if present as a string. */
export function optionValueName(value: unknown): string | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const name: unknown = Reflect.get(value, 'name');
  return typeof name === 'string' ? name : undefined;
}

/**
 * Projects a resolved item list to `{ id, label, optionValue }` triples. `id` is
 * `getOptionValue(item).id` (the identity under the `{ id, name }` regime),
 * `label` is `getOptionLabel(item)` (falling back to the projected `.name` and
 * finally the id so a picker never renders a blank row), and `optionValue` is the
 * WHOLE projected value object, preserved so the selection handler can write it to
 * the form instead of throwing everything but the id away.
 *
 * Items whose projected value carries no string `.id` are dropped: they violate
 * the contract-suite identity assertion and cannot be selected by a stable key.
 *
 * Pure data function: same items -> same options, no IO.
 */
export function toResolvedOptions<TItem, TValue>(
  source: OptionSource<TItem, TValue>,
  items: readonly TItem[],
): ResolvedOption<TItem, TValue>[] {
  const options: ResolvedOption<TItem, TValue>[] = [];
  for (const item of items) {
    const projected = source.getOptionValue(item);
    const id = optionValueId(projected);
    if (id === undefined) continue;
    const label =
      source.getOptionLabel(item) || optionValueName(projected) || id;
    options.push({ id, item, label, optionValue: projected });
  }
  return options;
}

/**
 * Projects a resolved item list to the flat `{ value, label }` options the BUI
 * widgets render. A thin view over {@link toResolvedOptions} that keeps only the
 * id (as `value`) and label. Pure data function: same items -> same options.
 */
export function toOptions<TItem, TValue>(
  source: OptionSource<TItem, TValue>,
  items: readonly TItem[],
): ProjectedOption[] {
  return toResolvedOptions(source, items).map(({ id, label }) => ({
    label,
    value: id,
  }));
}
